import axios from 'axios';

// REACT_APP_API_URL should be the base origin only (e.g. https://localhost:63857).
// Single source of truth for the backend origin — every page that needs it
// (admin panel included) imports API_ORIGIN from here instead of redefining
// its own fallback.
export const API_ORIGIN = process.env.REACT_APP_API_URL || 'https://localhost:63857';

// The /api prefix is appended here so it is consistent across the whole app
const API = axios.create({
  baseURL: `${API_ORIGIN}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

// ASP.NET validation failures arrive as { errors: { Field: ['message', ...] } }
function firstValidationMessage(data) {
  const errors = data?.errors;
  if (!errors || typeof errors !== 'object') return null;
  const first = Object.values(errors).flat()[0];
  return typeof first === 'string' ? first : null;
}

// Surface backend error messages instead of generic axios errors
API.interceptors.response.use(
  (res) => res,
  (err) => {
    const message =
      err.response?.data?.message ||
      err.response?.data?.error ||
      (err.code === 'ECONNABORTED' ? 'Request timed out. Please try again.' : null) ||
      (err.message === 'Network Error'
        ? 'Unable to reach the server. Check your connection.'
        : null) ||
      err.message;
    const error = new Error(firstValidationMessage(err.response?.data) || message);
    // Field-level messages from the API's validation problem details, when present.
    error.fieldErrors = err.response?.data?.errors;
    error.status = err.response?.status;
    return Promise.reject(error);
  }
);

export const submitEnrollment = (data, resumeFile) => {
  const form = new FormData();
  Object.entries(data).forEach(([key, val]) => {
    if (val !== null && val !== undefined && val !== '') {
      form.append(key, val);
    }
  });
  if (resumeFile) form.append('resume', resumeFile);
  return API.post('/enrollment', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// IdeaToImpact application: multipart form with the synopsis file and any extra team members.
export const submitIdeaApplication = (data, synopsisFile, teamMembers = []) => {
  const form = new FormData();
  Object.entries(data).forEach(([key, val]) => {
    if (val !== null && val !== undefined && val !== '') form.append(key, val);
  });
  teamMembers.forEach((m, i) => {
    form.append(`TeamMembers[${i}].Name`, m.name);
    if (m.email) form.append(`TeamMembers[${i}].Email`, m.email);
    if (m.phone) form.append(`TeamMembers[${i}].Phone`, m.phone);
  });
  form.append('synopsis', synopsisFile);
  return API.post('/ideatoimpact', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000, // the synopsis can be up to 5 MB on a slow connection
  });
};

export const submitContact = (data) => API.post('/contact', data);

// Project Development Assistance registration. Payment is collected offline, so nothing about
// payment is ever sent here.
export const submitProjectRegistration = (data) => API.post('/projectassistance', data);

// Public: the last date to apply to IdeaToImpact, as saved by an admin.
export const getIdeaToImpactSettings = () => API.get('/ideatoimpact/settings');

// Public Careers pages: open jobs, and one job by its Job ID (for example EAT-0007).
export const getJobs = () => API.get('/careers');
export const getJob = (jobCode) => API.get(`/careers/${encodeURIComponent(jobCode)}`);

// ── Analytics ──────────────────────────────────────────────
// Base origin for the tracking endpoint (sendBeacon needs an absolute URL).
const TRACK_URL = `${API_ORIGIN}/api/analytics/track`;

/**
 * Sends an analytics event. Uses navigator.sendBeacon so it survives page
 * unload (for time-on-page); falls back to fetch with keepalive. All errors
 * are swallowed — analytics must never disrupt the visitor experience.
 */
export const trackAnalytics = (payload) => {
  try {
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(TRACK_URL, blob);
    } else {
      fetch(TRACK_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    /* ignore */
  }
};

export default API;
